import { useTranslation } from 'react-i18next'
import classnames from 'classnames'

import { NUMBER_SIZE } from '../../../constants/size'
import { stopPropagation } from '../../../utils/events'
import {
  isAudioFile,
  isImageFile,
  isPdfFile,
  isVideoFile,
} from '../../../utils/file'
import Button, { BUTTON_HEIGHT_24 } from '../../Button'
import {
  FileImagePreviewError,
  FilePreviewImage,
  FilePreviewVideo,
} from '../../File'
import { FileIconPreview, FileIconType } from '../../FileIcon'
import { FloatingTooltip } from '../../Floating'
import { ICON_HEIGHT_32 } from '../../Icon'
import Text from '../../Text'
import { FILES_GALLERY_REMOVE_FILE_BTN_QA_NAME } from '../FilesContentGallery.constants'

import { getFileSrc, getImageFit } from './FileListItem.utils'

import * as styles from './FileListItem.module.css'

import DeleteIcon from '../../../icons/delete.svg'
import DownloadIcon from '../../../icons/export.svg'

function FileListItem({
  active,
  className,
  file,
  gallery,
  onClick,
  onDownloadClick,
  onRemove,
}) {
  const { t } = useTranslation()

  const imageFile = isImageFile(file)
  const videoFile = isVideoFile(file)

  const itemClassName = classnames(
    styles.FileListItem,
    {
      [styles.fullSize]: gallery,
    },
    { [styles.active]: active && !gallery },
    className
  )

  function handleClick() {
    onClick(file.id)
  }

  function handleClickDownload(e) {
    stopPropagation(e)

    onDownloadClick()
  }

  function handleClickRemove(e) {
    stopPropagation(e)

    onRemove(file)
  }

  const src = getFileSrc(file, gallery)

  const fit = getImageFit(file)

  return (
    <FloatingTooltip content={file.filename}>
      <div className={itemClassName} onClick={handleClick}>
        {imageFile ? (
          <FilePreviewImage
            ErrorImagePreview={FileImagePreviewError}
            alt={file.filename}
            className={styles.imageContainer}
            errorPreviewImageProps={{
              className: styles.errorPreviewContainer,
              size: gallery ? NUMBER_SIZE.size44 : NUMBER_SIZE.size32,
            }}
            filled={gallery}
            fit={fit}
            imageClassName={styles.image}
            src={src || file.origin}
          />
        ) : null}
        {videoFile ? (
          <FilePreviewVideo
            ErrorImagePreview={FileImagePreviewError}
            className={styles.image}
            data={file}
            errorPreviewImageProps={{
              className: styles.errorPreviewContainer,
              iconType: FileIconType.Video,
              size: gallery ? NUMBER_SIZE.size44 : NUMBER_SIZE.size32,
            }}
            iconSize={ICON_HEIGHT_32}
          />
        ) : null}
        {!imageFile && !videoFile ? (
          <>
            <FileIconPreview
              file={file}
              size={gallery ? NUMBER_SIZE.size44 : NUMBER_SIZE.size32}
            />
            {gallery ? (
              <div className={styles.textContainer}>
                {isPdfFile(file) || isAudioFile(file) ? (
                  <>
                    <Text isSecondary>{file.filename}</Text>
                    <Button.Text
                      className={styles.button}
                      text={t('Files.OpenPreviewTextBtnKey')}
                      onClick={handleClick}
                    />
                  </>
                ) : (
                  <>
                    <Text isSecondary>
                      {t('Newsboard.PreviewNotSupportedTextMessageKey')}
                    </Text>
                    <Text isSecondary>
                      {t('Newsboard.PreviewDownloadTextMessageKey')}
                    </Text>
                    <Button.Text
                      beforeIconProps={{ Icon: DownloadIcon }}
                      className={styles.button}
                      text={t('Files.DownloadBtnKey')}
                      onClick={handleClickDownload}
                    />
                  </>
                )}
              </div>
            ) : null}
          </>
        ) : null}
        {onRemove ? (
          <Button.Outlined
            isGrey
            beforeIconProps={{ Icon: DeleteIcon }}
            className={styles.removeButton}
            height={BUTTON_HEIGHT_24}
            qaName={FILES_GALLERY_REMOVE_FILE_BTN_QA_NAME}
            tooltip={t('FilesGallery.RemoveFileBtnTooltipKey')}
            onClick={handleClickRemove}
          />
        ) : null}
      </div>
    </FloatingTooltip>
  )
}

export default FileListItem
